<script setup>
import { ref, watch } from 'vue'
import { api } from '../../services/api'
import ConfirmDialog from '../ConfirmDialog.vue'

const props = defineProps({ student: { type: Object, required: true } })
const emit = defineEmits(['notice'])
const data = ref(null)
const error = ref('')
const openSubjects = ref(new Set())
const resetTarget = ref(null)
const resetting = ref(false)
const resetError = ref('')

async function load() {
  data.value = null
  error.value = ''
  try {
    data.value = await api(`/admin/students/${props.student.id}/progress`)
    openSubjects.value = new Set(data.value.subjects.slice(0, 1).map(subject => subject.id))
  } catch (err) { error.value = err.message }
}

function toggle(id) {
  const next = new Set(openSubjects.value)
  next.has(id) ? next.delete(id) : next.add(id)
  openSubjects.value = next
}

function startedLessons(subject) {
  return subject.semesters.flatMap(sem => sem.chapters.flatMap(chapter => chapter.lessons.filter(lesson => lesson.status !== 'not_started')))
}

async function resetLesson() {
  if (resetting.value) return
  resetting.value = true
  resetError.value = ''
  try {
    const result = await api(`/admin/students/${props.student.id}/lessons/${resetTarget.value.id}/reset`, { method: 'POST' })
    resetTarget.value = null
    emit('notice', result.message)
    await load()
  } catch (err) { resetError.value = err.message }
  finally { resetting.value = false }
}

watch(() => props.student.id, load, { immediate: true })
</script>

<template>
  <div class="student-panel">
    <div v-if="error" class="error-banner" role="alert">{{ error }} <button class="btn btn-light" @click="load">Thử lại</button></div>
    <div v-if="!data && !error" class="loading-block small"><div><div class="loader"></div><p>Đang tải tiến độ…</p></div></div>
    <template v-else-if="data">
      <div class="student-stats">
        <div><strong>{{ data.stats.completed_lessons }}</strong><span>Bài xong</span></div>
        <div><strong>{{ data.stats.study_minutes }}</strong><span>Phút học</span></div>
        <div><strong>{{ data.stats.average_score }}%</strong><span>Điểm TB</span></div>
        <div><strong>{{ data.stats.streak_days }}</strong><span>Ngày liên tiếp</span></div>
      </div>
      <p v-if="!data.subjects.length" class="form-help">Chưa có môn nào được mở cho lớp {{ student.grade }}.</p>
      <section v-for="subject in data.subjects" :key="subject.id" class="student-subject">
        <button type="button" class="student-subject-head" :aria-expanded="openSubjects.has(subject.id)" @click="toggle(subject.id)">
          <span>{{ subject.icon }} {{ subject.title }}</span><small>{{ subject.progress.completed }}/{{ subject.progress.total }} bài</small><b :class="{ open: openSubjects.has(subject.id) }">›</b>
        </button>
        <div class="progress-track"><span :style="{ width: `${subject.progress.percent}%` }"></span></div>
        <div v-if="openSubjects.has(subject.id)" class="student-lessons">
          <p v-if="!startedLessons(subject).length" class="form-help">Em chưa bắt đầu bài nào của môn này.</p>
          <div v-for="lesson in startedLessons(subject)" :key="lesson.id" class="student-lesson">
            <span class="lesson-state" :class="{ done: lesson.status === 'completed', active: lesson.status === 'in_progress' }">{{ lesson.status === 'completed' ? '✓' : '•' }}</span>
            <div><strong>{{ lesson.title }}</strong><small>{{ lesson.status === 'completed' ? 'Đã hoàn thành' : `Đang học ${lesson.progress}%` }}</small></div>
            <button type="button" class="text-link" @click="resetTarget = lesson; resetError = ''">↺ Cho học lại</button>
          </div>
        </div>
      </section>
      <h3 class="student-attempts-title">Bài làm gần đây</h3>
      <p v-if="!data.attempts.length" class="form-help">Chưa có bài làm.</p>
      <div v-for="attempt in data.attempts" :key="attempt.id" class="student-attempt"><span>{{ attempt.title }}</span><small>{{ attempt.date }}</small><b class="score-pill" :class="{ low: attempt.percent < 60 }">{{ attempt.percent }}%</b></div>
    </template>
  </div>
  <ConfirmDialog :open="!!resetTarget" title="Cho học lại bài này?" confirm-label="Đặt lại tiến độ" busy-label="Đang đặt lại…" :busy="resetting" :error="resetError" @cancel="resetTarget = null" @confirm="resetLesson">
    <div class="reset-confirm-targets">
      <div><span>Học sinh</span><strong>{{ student.full_name }}</strong></div>
      <div><span>Bài học</span><strong>{{ resetTarget?.title }}</strong></div>
    </div>
    <p>Tiến độ bài học sẽ về 0% để học sinh bắt đầu lại. Lịch sử và điểm luyện tập vẫn được giữ lại.</p>
  </ConfirmDialog>
</template>

<style scoped>
.student-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin-bottom: 18px; }
.student-stats > div { padding: 10px; border-radius: 12px; background: #f7f6fd; text-align: center; }
.student-stats strong, .student-stats span { display: block; }
.student-stats strong { font-size: 18px; }
.student-stats span { margin-top: 3px; color: var(--muted); font-size: 10px; }
.student-subject { padding: 12px 0; border-top: 1px solid var(--border); }
.student-subject-head { width: 100%; display: flex; align-items: center; gap: 8px; margin-bottom: 8px; padding: 0; border: 0; background: none; text-align: left; cursor: pointer; }
.student-subject-head span { flex: 1; font-size: 13px; font-weight: 700; }
.student-subject-head small { color: var(--muted); font-size: 11px; }
.student-subject-head b { color: #9a9fb5; transition: transform .15s; }
.student-subject-head b.open { transform: rotate(90deg); }
.student-lessons { margin-top: 8px; }
.student-lesson { display: grid; grid-template-columns: 26px minmax(0, 1fr) auto; align-items: center; gap: 10px; padding: 8px 0; }
.student-lesson strong, .student-lesson small { display: block; }
.student-lesson strong { font-size: 12px; }
.student-lesson small { margin-top: 2px; color: var(--muted); font-size: 10px; }
.student-lesson .text-link { border: 0; background: none; cursor: pointer; font-size: 11px; white-space: nowrap; }
.student-attempts-title { margin: 18px 0 8px; font-size: 14px; }
.student-attempt { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-top: 1px solid var(--border); font-size: 12px; }
.student-attempt span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.student-attempt small { color: var(--muted); font-size: 10px; }
</style>
